import type { ReactNode, SVGProps } from "react";

import type { AgentGlyph } from "@/lib/people";

/*
 * The quiet layer's line icons, drawn on a 16-unit grid with a 1.5 stroke.
 * They come from the approved mock (appnotes-quiet-layer--2026-10-15), so the
 * shell and the open note match the renders stroke for stroke.
 */
type IconProps = SVGProps<SVGSVGElement>;

function LineIcon({ children, className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={
        className === undefined ? "line-icon" : `line-icon ${className}`
      }
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      viewBox="0 0 16 16"
      {...props}
    >
      {children}
    </svg>
  );
}

const bubble =
  "M3 3.5h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H7l-3 2.5v-2.5H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Z";

export const NotesIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d={bubble} />
  </LineIcon>
);

export const ReplyIcon = NotesIcon;

export const SitesIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <circle cx="8" cy="8" r="6" />
    <path d="M2 8h12M8 2c1.8 1.7 2.6 3.7 2.6 6S9.8 12.3 8 14C6.2 12.3 5.4 10.3 5.4 8S6.2 3.7 8 2Z" />
  </LineIcon>
);

export const AccessIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <circle cx="5.5" cy="10.5" r="3" />
    <path d="m7.6 8.4 5.9-5.9M11.5 4.5l1.6 1.6M10 6l1.2 1.2" />
  </LineIcon>
);

export const TeamIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <circle cx="6" cy="5.5" r="2.5" />
    <path d="M1.8 13.5c.5-2.3 2.2-3.5 4.2-3.5s3.7 1.2 4.2 3.5" />
    <path d="M10.5 3.2a2.4 2.4 0 0 1 0 4.6M12 10.2c1.2.5 2 1.6 2.3 3.3" />
  </LineIcon>
);

export const BillingIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <rect height="9" rx="1.5" width="12" x="2" y="3.5" />
    <path d="M2 6.5h12M4.5 10h2" />
  </LineIcon>
);

export const ChevronDownIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="m4.5 6.5 3.5 3.5 3.5-3.5" />
  </LineIcon>
);

export const UpDownIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="m5 6 3-3 3 3M5 10l3 3 3-3" />
  </LineIcon>
);

export const BackIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="M10 3.5 5.5 8l4.5 4.5" />
  </LineIcon>
);

export const CheckIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="m3.5 8.5 3 3 6-7" />
  </LineIcon>
);

export const ExternalIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="M6 3.5H3.5v9h9V10M9 3h4v4M13 3 7.5 8.5" />
  </LineIcon>
);

export const SendIcon = (props: IconProps) => (
  <LineIcon strokeWidth={1.8} {...props}>
    <path d="M8 13V3.5M4 7.5l4-4 4 4" />
  </LineIcon>
);

export const MoreIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <circle cx="3.5" cy="8" r=".6" fill="currentColor" />
    <circle cx="8" cy="8" r=".6" fill="currentColor" />
    <circle cx="12.5" cy="8" r=".6" fill="currentColor" />
  </LineIcon>
);

export const PlusIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <path d="M8 3v10M3 8h10" />
  </LineIcon>
);

export const CopyIcon = (props: IconProps) => (
  <LineIcon {...props}>
    <rect height="8" rx="1.5" width="8" x="5.5" y="5.5" />
    <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
  </LineIcon>
);

/* Agent client marks: monochrome, drawn in-house, no brand files. */

function rays(): string {
  const parts: string[] = [];
  for (let index = 0; index < 10; index++) {
    const angle = (index / 10) * Math.PI * 2 - Math.PI / 2;
    const outer = index % 2 === 0 ? 6 : 4.8;
    const x1 = 8 + Math.cos(angle) * 1.4;
    const y1 = 8 + Math.sin(angle) * 1.4;
    const x2 = 8 + Math.cos(angle) * outer;
    const y2 = 8 + Math.sin(angle) * outer;
    parts.push(
      `M${x1.toFixed(2)} ${y1.toFixed(2)}L${x2.toFixed(2)} ${y2.toFixed(2)}`,
    );
  }
  return parts.join("");
}

const claudeRays = rays();

const glyphs: Record<AgentGlyph, () => ReactNode> = {
  claude: () => <path d={claudeRays} />,
  codex: () => (
    <g>
      <ellipse cx="8" cy="8" rx="1.9" ry="5.4" />
      <ellipse cx="8" cy="8" rx="1.9" ry="5.4" transform="rotate(60 8 8)" />
      <ellipse cx="8" cy="8" rx="1.9" ry="5.4" transform="rotate(120 8 8)" />
    </g>
  ),
  cursor: () => (
    <path d="M8 2 13.2 5v6L8 14l-5.2-3V5ZM8 8V14M8 8l5.2-3M8 8 2.8 5" />
  ),
  terminal: () => <path d="m3.5 5 3 3-3 3M8.5 11.5h4" />,
};

/** The client mark inside an agent's square. */
export function AgentGlyphIcon({
  glyph,
  ...props
}: IconProps & { readonly glyph: AgentGlyph }) {
  const Glyph = glyphs[glyph];
  return (
    <LineIcon data-glyph={glyph} strokeWidth={1.6} {...props}>
      <Glyph />
    </LineIcon>
  );
}
